import { env } from "@/env";
import { Nullish } from "@/models";
import Dexie from 'dexie';

import {
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";
import { clearUserDb } from './db';

class AuthService {

  private initializeDatabaseCleanup() {
    Dexie.getDatabaseNames()
      .then(names => names.forEach(name => {
          Dexie.delete(name);
      }));
  }

  private readonly config: UserManagerSettings = {
    authority: env.auth.authority,
    client_id: env.auth.clientId,
    response_type: env.auth.responseType,
    scope: env.auth.scope,
    userStore: new WebStorageStateStore({ store: localStorage }),
    automaticSilentRenew: true,
    redirect_uri: new URL("/login-complete", window.location.origin).href,
    post_logout_redirect_uri: new URL("/", window.location.origin).href,
    silent_redirect_uri: new URL("/silent-refresh", window.location.origin)
      .href,
    client_secret: null as unknown as string,
  };
  private readonly userManager = new UserManager(this.config);

  get loggedIn(): Promise<boolean> {
    return this.user.then((user) => !!user);
  }
  get token(): Promise<Nullish<string>> {
    return this.user.then(async (u) => {
      if (u) return u.access_token;
      await this.login();
      return null;
    });
  }

  loginIfNeeded(): Promise<void> {
    return this.loggedIn.then((loggedIn) => {
      if (!loggedIn) {
        return this.login();
      } else {
        this.notifyParent();
      }
    });
  }

  login(): Promise<void> {
    this.initializeDatabaseCleanup();
    return this.userManager.signinRedirect();
  }

  async logout(): Promise<void> {
    await clearUserDb();
    await this.userManager.removeUser();
    await this.userManager.revokeTokens(['access_token', 'refresh_token']);
    await this.userManager.signoutRedirect({
        extraQueryParams: {
            client_id: this.userManager.settings.client_id,
            logout_uri: window.location.origin
        }
    });
  }

  onLogin(): Promise<boolean> {
    return this.userManager.signinRedirectCallback().then(() => {
      this.notifyParent();
      return true;
    });
  }

  onSilentRefresh(): Promise<boolean> {
    return this.userManager.signinSilentCallback().then(() => true);
  }

  get user(): Promise<Nullish<User>> {
    return this.userManager.getUser().then(async (user) => {
      if (!user || user.expired) {
        await this.login();
        return null;
      }

      return user;
    });
  }

  private notifyParent() {
    parent.postMessage("auth:login");
  }
}

const globalForAuth = globalThis as typeof globalThis & { auth: AuthService };

export const auth = globalForAuth.auth ?? new AuthService();

if (env.mode === "production") {
  globalForAuth.auth = auth;
}
